// One row returned from the IMPACT read-through search (CSS.DBT joined to
// CSS.DEM [DEM_ID='A0'] and CSS.CLT). Records come straight from the ADF
// pipeline's Lookup output and are never stored in the local database.
export interface IImpactDebtor {
  dbT_NO: string;
  dbT_COMPANY: string;
  dbT_CLIENT: string;
  dbT_CLNT_ACNT1: string;
  dbT_CLNT_ACNT2: string;
  dbT_NAME: string;
  dbT_STATUS: string;
  dbT_REFERRAL_DATE?: string;
  dbT_REFERRAL_AMT?: number;
  dbT_PRINCIPAL_DUE?: number;
  dbT_PRINCIPAL_RECVD?: number;
  dbT_MISC1_DUE?: number;
  dbT_LAST_CHG_DATE?: string;
  dbT_LAST_PAY_DATE?: string;
  dbT_FLD_24?: string;
  dbT_FLD_04?: string;
  dbT_TOTAL_DUE?: number;
  deM_ID?: string;
  deM_NAME?: string;
  deM_MEMO?: string;
  deM_ADDR1?: string;
  deM_ADDR2?: string;
  deM_CITY?: string;
  deM_STATE?: string;
  deM_ZIP?: string;
  deM_PHONE1?: string;
  deM_EMAIL?: string;
  deM_TAX_ID?: string;
  deM_DOB?: string;
  clT_NAME_1?: string;
  stS_DESC?: string;
  isActive?: boolean;
}

export interface IImpactSearchResult {
  requestId: string;
  status: string;
  adfRunId?: string;
  recordCount?: number;
  isExistingRequest?: boolean;
  message?: string;
  records?: IImpactDebtor[];
}

export type ImpactSearchState = 'idle' | 'searching' | 'noRecords' | 'error';

// Matches the local search proc's phone formatting:
// 10+ digit values render as 000-000-0000, shorter values pass through.
const formatImpactPhone = (phone?: string): string => {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6)}`;
  }
  return phone ?? '';
};

// Shapes an IMPACT record like a local search row (IDebtor / IAgentDebtor)
// so the existing grid, ExpandableRow, and detail flow render it unchanged.
// Required numeric/string fields are defaulted so the row component's
// formatting (toFixed, replace, toLocaleLowerCase) never hits undefined.
export const mapImpactDebtorToRow = (record: IImpactDebtor): any => ({
  dbT_COMPANY: record.dbT_COMPANY ?? '',
  dbT_CLIENT: record.dbT_CLIENT ?? '',
  dbT_NO: record.dbT_NO ?? '',
  dbT_CLNT_ACNT1: record.dbT_CLNT_ACNT1 ?? '',
  dbT_CLNT_ACNT2: record.dbT_CLNT_ACNT2 ?? '',
  dbT_FLD_24: record.dbT_FLD_24 ?? '',
  dbT_FLD_04: record.dbT_FLD_04 ?? '',
  dbT_NAME: record.dbT_NAME ?? '',
  clT_NAME_1: record.clT_NAME_1 ?? '',
  deM_TAX_ID: record.deM_TAX_ID ?? '',
  deM_NAME: record.deM_NAME ?? '',
  deM_MEMO: record.deM_MEMO ?? '',
  deM_ADDR1: record.deM_ADDR1 ?? '',
  deM_ADDR2: record.deM_ADDR2 ?? '',
  deM_CITY: record.deM_CITY ?? '',
  deM_STATE: record.deM_STATE ?? '',
  deM_ZIP: record.deM_ZIP ?? '',
  deM_PHONE1: formatImpactPhone(record.deM_PHONE1),
  deM_EMAIL: record.deM_EMAIL ?? '',
  deM_DOB: record.deM_DOB ?? '',
  deM_ID: record.deM_ID ?? 'A0',
  dbT_LAST_CHG_DATE: record.dbT_LAST_CHG_DATE,
  dbT_REFERRAL_DATE: record.dbT_REFERRAL_DATE,
  dbT_REFERRAL_AMT: record.dbT_REFERRAL_AMT ?? 0,
  dbT_PRINCIPAL_DUE: record.dbT_PRINCIPAL_DUE ?? 0,
  dbT_PRINCIPAL_RECVD: record.dbT_PRINCIPAL_RECVD ?? 0,
  dbT_MISC1_DUE: record.dbT_MISC1_DUE ?? 0,
  dbT_TOTAL_DUE: record.dbT_TOTAL_DUE ?? 0,
  stS_DESC: record.stS_DESC ?? record.dbT_STATUS ?? '',
  dbT_Status: record.dbT_STATUS ?? '',
  isActive: record.isActive ?? false,
});

export const ImpactSearchMessages = {
  initial: 'No matching records were found in the local database. You can search the IMPACT system to retrieve additional records.',
  searching: 'Searching the IMPACT system. This may take a few moments.',
  success: 'Records were successfully retrieved from the IMPACT system.',
  noRecords: 'No matching records were found in either the local database or the IMPACT system.',
  error: 'Unable to retrieve records from the IMPACT system. Please try again later or contact your administrator.',
  cancelled: 'The IMPACT system search was cancelled.',
  selectField: 'Select a specific search field to search the IMPACT system.',
};
